import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Sparkles, LayoutDashboard, UserPlus, User, Database,
    Shield, MessageSquare, Plug, Building, PartyPopper,
    ChevronRight, ChevronLeft, X,
} from 'lucide-react';
import { useTutorial } from '../contexts/TutorialContext';
import { useTranslation } from 'react-i18next';

// Map step icon names to Lucide components
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const iconMap: Record<string, any> = {
    Sparkles, LayoutDashboard, UserPlus, User, Database,
    Shield, MessageSquare, Plug, Building, PartyPopper,
};

// ─── Spotlight effect ───────────────────────────────────────────────────────
const SpotlightOverlay: React.FC<{ selector?: string }> = ({ selector }) => {
    const [rect, setRect] = useState<DOMRect | null>(null);

    useEffect(() => {
        if (!selector) { setRect(null); return; }
        const el = document.querySelector(selector);
        if (el) {
            const r = el.getBoundingClientRect();
            setRect(r);
            el.classList.add('tutorial-spotlight-target');
            return () => el.classList.remove('tutorial-spotlight-target');
        } else {
            setRect(null);
        }
    }, [selector]);

    if (!rect) {
        // Full overlay, no cutout
        return <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-[2px] transition-all duration-500" />;
    }

    const pad = 12;
    const x = rect.left - pad;
    const y = rect.top - pad;
    const w = rect.width + pad * 2;
    const h = rect.height + pad * 2;
    const r = 16; // border-radius of cutout

    return (
        <svg className="fixed inset-0 z-[9998] w-full h-full" style={{ pointerEvents: 'auto' }}>
            <defs>
                <mask id="tutorial-spotlight-mask">
                    <rect x="0" y="0" width="100%" height="100%" fill="white" />
                    <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="black" />
                </mask>
            </defs>
            <rect
                x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.60)"
                mask="url(#tutorial-spotlight-mask)"
                style={{ backdropFilter: 'blur(2px)' }}
            />
            {/* Glow ring around spotlight */}
            <rect
                x={x - 2} y={y - 2} width={w + 4} height={h + 4}
                rx={r + 2} ry={r + 2}
                fill="none"
                stroke="var(--accent)"
                strokeWidth="2"
                opacity="0.6"
                className="animate-pulse"
            />
        </svg>
    );
};

// ─── Confetti burst for the final step ──────────────────────────────────────
const ConfettiBurst: React.FC = () => {
    const particles = Array.from({ length: 40 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 1.5 + Math.random() * 1.5,
        color: ['#6366f1', '#f59e0b', '#10b981', '#ef4444', 'var(--accent)', '#8b5cf6', '#ec4899'][i % 7],
        size: 4 + Math.random() * 6,
    }));

    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {particles.map(p => (
                <motion.div
                    key={p.id}
                    className="absolute rounded-full"
                    style={{
                        left: `${p.x}%`,
                        top: '-5%',
                        width: p.size,
                        height: p.size,
                        backgroundColor: p.color,
                    }}
                    initial={{ y: 0, opacity: 1, rotate: 0 }}
                    animate={{
                        y: [0, 400 + Math.random() * 300],
                        x: [0, (Math.random() - 0.5) * 200],
                        opacity: [1, 1, 0],
                        rotate: [0, 360 * (Math.random() > 0.5 ? 1 : -1)],
                    }}
                    transition={{
                        duration: p.duration,
                        delay: p.delay,
                        ease: 'easeOut',
                    }}
                />
            ))}
        </div>
    );
};

// ─── Step Card Content ──────────────────────────────────────────────────────
const StepContent: React.FC<{
    stepIndex: number;
    isLast: boolean;
}> = ({ stepIndex, isLast }) => {
    const { steps, nextStep, prevStep, skipTutorial, completeTutorial, totalSteps } = useTutorial();
    const { t } = useTranslation('dashboard');
    const step = steps[stepIndex];
    const Icon = iconMap[step.icon || 'Sparkles'] || Sparkles;
    const progress = ((stepIndex + 1) / totalSteps) * 100;

    const isWelcome = stepIndex === 0;

    return (
        <motion.div
            key={step.id}
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
        >
            {/* Progress bar */}
            <div className="h-1 bg-white/[0.06] rounded-full mb-6 overflow-hidden">
                <motion.div
                    className="h-full bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-400 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                />
            </div>

            {/* Icon + Step Counter */}
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isLast
                        ? 'bg-gradient-to-br from-amber-400/20 to-orange-500/20'
                        : isWelcome
                            ? 'bg-gradient-to-br from-indigo-500/20 to-purple-500/20'
                            : 'bg-hig-accent/10'
                        }`}>
                        <Icon size={24} className={
                            isLast ? 'text-amber-400' : isWelcome ? 'text-indigo-400' : 'text-hig-accent'
                        } />
                    </div>
                    <span className="text-xs font-medium text-hig-text-tertiary tracking-wider uppercase">
                        {t('tutorial.step_counter', { current: stepIndex + 1, total: totalSteps })}
                    </span>
                </div>

                <button
                    onClick={skipTutorial}
                    className="p-2 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-white/[0.06] rounded-xl transition-colors"
                    title={t('tutorial.skip')}
                >
                    <X size={18} />
                </button>
            </div>

            {/* Title */}
            <h2 className={`font-bold text-hig-text-primary mb-3 tracking-tight leading-tight ${isWelcome || isLast ? 'text-2xl lg:text-3xl' : 'text-xl lg:text-2xl'
                }`}>
                {t(step.titleKey)}
            </h2>

            {/* Description */}
            <p className="text-hig-text-secondary leading-relaxed mb-8 text-sm lg:text-base">
                {t(step.descriptionKey)}
            </p>

            {/* Confetti on last step */}
            {isLast && <ConfettiBurst />}

            {/* Navigation buttons */}
            <div className="flex items-center justify-between">
                <div>
                    {stepIndex > 0 && (
                        <button
                            onClick={prevStep}
                            className="flex items-center gap-1.5 text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors px-3 py-2 rounded-xl hover:bg-white/[0.04]"
                        >
                            <ChevronLeft size={16} />
                            {t('tutorial.back')}
                        </button>
                    )}
                    {stepIndex === 0 && (
                        <button
                            onClick={skipTutorial}
                            className="text-sm text-hig-text-tertiary hover:text-hig-text-secondary transition-colors px-3 py-2 rounded-xl"
                        >
                            {t('tutorial.skip')}
                        </button>
                    )}
                </div>

                <button
                    onClick={isLast ? completeTutorial : nextStep}
                    className={`flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 shadow-lg ${isLast
                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-400 hover:to-orange-400 shadow-amber-500/25'
                        : 'bg-gradient-to-r from-indigo-500 to-blue-500 text-white hover:from-indigo-400 hover:to-blue-400 shadow-indigo-500/25'
                        }`}
                >
                    {isLast ? t('tutorial.get_started') : t('tutorial.next')}
                    {!isLast && <ChevronRight size={16} />}
                </button>
            </div>

            {/* Step dots */}
            <div className="flex items-center justify-center gap-1.5 mt-6">
                {steps.map((_, idx) => (
                    <div
                        key={idx}
                        className={`rounded-full transition-all duration-300 ${idx === stepIndex
                            ? 'w-6 h-2 bg-hig-accent'
                            : idx < stepIndex
                                ? 'w-2 h-2 bg-hig-accent/40'
                                : 'w-2 h-2 bg-white/[0.1]'
                            }`}
                    />
                ))}
            </div>
        </motion.div>
    );
};

// ─── Main Tutorial Wizard ───────────────────────────────────────────────────
const TutorialWizard: React.FC = () => {
    const { isActive, currentStep, steps } = useTutorial();
    const cardRef = useRef<HTMLDivElement>(null);

    if (!isActive) return null;

    const step = steps[currentStep];
    const isCentered = !step.highlightSelector || step.position === 'center';

    return (
        <>
            {/* Overlay + Spotlight */}
            <SpotlightOverlay selector={step.highlightSelector} />

            {/* Tutorial Card */}
            <div
                className={`fixed z-[9999] ${isCentered
                    ? 'inset-0 flex items-center justify-center p-4'
                    : 'bottom-6 right-6 lg:bottom-10 lg:right-10'
                    }`}
                style={{ pointerEvents: 'none' }}
            >
                <div
                    ref={cardRef}
                    className="w-full max-w-lg"
                    style={{ pointerEvents: 'auto' }}
                >
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={`card-${currentStep}`}
                            className="bg-hig-bg-secondary/95 backdrop-blur-2xl rounded-3xl border border-white/[0.08] shadow-2xl p-8 lg:p-10"
                            style={{
                                boxShadow: '0 25px 80px -12px rgba(0,0,0,0.5), 0 0 40px -8px rgba(99,102,241,0.15)',
                            }}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                        >
                            <StepContent
                                stepIndex={currentStep}
                                isLast={currentStep === steps.length - 1}
                            />
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </>
    );
};

export default TutorialWizard;
